/**
 * The `excerpt` mark (#277): excerpted text stays in its source document,
 * carrying `{ id, kind }`. Several marks may share text (`excludes: ''`), so
 * excerpts nest and overlap; typing at an edge does not grow one
 * (`inclusive: false`).
 *
 * A mark only shows while its excerpt is live: the host puts the live ids in
 * `storage.live` (from GET /api/excerpts/by-source) and dispatches
 * `refreshExcerpts`. A mark whose excerpt was removed - or deleted with its
 * task - stays in the JSON and renders as plain text, which is what lets an
 * undo bring it back by restoring rows alone.
 *
 * Live excerpts get a tint over their text and an icon before their first
 * character; several starting at one point share an icon with a count.
 *
 * Two guards: pasted content loses its excerpt marks and embeds (a copy is
 * not the excerpt; the same id in two places would stretch its range over
 * everything between), and an edit that would wipe out more than one live
 * excerpt asks first.
 */

import { Mark, mergeAttributes, type Editor } from '@tiptap/core';
import { Plugin, PluginKey, type EditorState, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import { Fragment, Slice, type Node as PmNode, type Mark as PmMark } from '@tiptap/pm/model';

export type ExcerptKind = 'excerpt' | 'task' | 'note';

export type TaskEndState = 'done' | 'cancelled';

export interface ExcerptMarkStorage {
  /** Live excerpt ids in this document, with their kind. */
  live: Map<string, ExcerptKind>;
  /** Task excerpts whose task is done or cancelled (#324); others show an open task icon. */
  taskStates: Map<string, TaskEndState>;
  /** Opens the excerpt menu for the icon that was clicked. */
  onIconClick: ((ids: string[], anchor: HTMLElement) => void) | null;
  /** Asked before an edit removes several live excerpts; false cancels the edit. */
  confirmDrop: ((count: number) => boolean) | null;
  /** Ctrl+Shift+E: open the destination picker for the selection. */
  onShortcut: (() => void) | null;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    excerpt: {
      setExcerpt: (attrs: { id: string; kind: ExcerptKind }) => ReturnType;
      unsetExcerpt: (id: string) => ReturnType;
      adjustExcerptRange: (id: string, from: number, to: number) => ReturnType;
      refreshExcerpts: () => ReturnType;
    };
  }
  interface Storage {
    excerpt: ExcerptMarkStorage;
  }
}

const KINDS = new Set<ExcerptKind>(['excerpt', 'task', 'note']);
const asKind = (k: unknown): ExcerptKind => (KINDS.has(k as ExcerptKind) ? (k as ExcerptKind) : 'excerpt');

export const excerptPluginKey = new PluginKey<DecorationSet>('excerpt');
const REFRESH = 'excerptRefresh';
export const SKIP_DROP_CONFIRM = 'excerptSkipDropConfirm';

/** First to last position carrying the mark, or null when absent. */
export function findExcerptRange(doc: PmNode, id: string): { from: number; to: number } | null {
  let from = -1;
  let to = -1;
  doc.descendants((node, pos) => {
    if (!node.isText) return true;
    if (node.marks.some((m) => m.type.name === 'excerpt' && m.attrs.id === id)) {
      if (from < 0) from = pos;
      to = pos + node.nodeSize;
    }
    return false;
  });
  return from < 0 ? null : { from, to };
}

/** Every excerpt id marked in the document. */
export function excerptIdsIn(doc: PmNode): Set<string> {
  const ids = new Set<string>();
  doc.descendants((node) => {
    if (node.isText) for (const m of node.marks) if (m.type.name === 'excerpt' && m.attrs.id) ids.add(m.attrs.id as string);
    return true;
  });
  return ids;
}

function removeIdMarks(tr: Transaction, id: string): void {
  const hits: Array<{ from: number; to: number; mark: PmMark }> = [];
  tr.doc.descendants((node, pos) => {
    if (!node.isText) return true;
    for (const m of node.marks) if (m.type.name === 'excerpt' && m.attrs.id === id) hits.push({ from: pos, to: pos + node.nodeSize, mark: m });
    return false;
  });
  for (const h of hits) tr.removeMark(h.from, h.to, h.mark);
}

/** The slice without excerpt marks or embeds; used for paste. */
export function stripExcerpts(fragment: Fragment): Fragment {
  const out: PmNode[] = [];
  fragment.forEach((node) => {
    if (node.type.name === 'excerptEmbed') return;
    if (node.isText) {
      out.push(node.mark(node.marks.filter((m) => m.type.name !== 'excerpt')));
      return;
    }
    out.push(node.copy(stripExcerpts(node.content)));
  });
  return Fragment.fromArray(out);
}

function buildDecorations(doc: PmNode, storage: ExcerptMarkStorage): DecorationSet {
  const { live, taskStates } = storage;
  const onIcon: ExcerptMarkStorage['onIconClick'] = (ids, a) => storage.onIconClick?.(ids, a);
  if (live.size === 0) return DecorationSet.empty;
  const decos: Decoration[] = [];
  const starts = new Map<number, string[]>();
  const seen = new Set<string>();
  doc.descendants((node, pos) => {
    if (!node.isText) return true;
    for (const m of node.marks) {
      if (m.type.name !== 'excerpt') continue;
      const id = m.attrs.id as string;
      const kind = live.get(id);
      if (!kind) continue;
      decos.push(Decoration.inline(pos, pos + node.nodeSize, { class: `excerpt-live excerpt-${kind}` }));
      if (!seen.has(id)) {
        seen.add(id);
        starts.set(pos, [...(starts.get(pos) ?? []), id]);
      }
    }
    return false;
  });
  for (const [pos, ids] of starts) {
    const kind = live.get(ids[0])!;
    const raw = kind === 'task' && ids.length === 1 ? taskStates.get(ids[0]) : undefined;
    const ended = raw === 'done' || raw === 'cancelled' ? raw : null;
    decos.push(Decoration.widget(pos, () => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `excerpt-icon excerpt-icon-${ids.length > 1 ? 'many' : kind}${ended ? `-${ended}` : ''}`;
      btn.contentEditable = 'false';
      btn.setAttribute('aria-label', ids.length > 1 ? `${ids.length} excerpts` : kind === 'excerpt' ? 'Excerpt' : `${kind === 'task' ? 'Task' : 'Note'} excerpt${ended ? ` (${ended})` : ''}`);
      btn.dataset.excerptIds = ids.join(',');
      if (ids.length > 1) btn.textContent = String(ids.length);
      btn.addEventListener('mousedown', (e) => e.preventDefault());
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        onIcon?.(ids, btn);
      });
      return btn;
    }, { side: -1, key: `excerpt-${ids.join(',')}-${kind}${ended ? `-${ended}` : ''}`, ignoreSelection: true }));
  }
  return DecorationSet.create(doc, decos);
}

function liveIdsRemoved(before: EditorState['doc'], after: PmNode, live: Map<string, ExcerptKind>): number {
  const was = [...excerptIdsIn(before)].filter((id) => live.has(id));
  if (was.length < 2) return 0;
  const now = excerptIdsIn(after);
  return was.filter((id) => !now.has(id)).length;
}

export const ExcerptMark = Mark.create<Record<string, never>, ExcerptMarkStorage>({
  name: 'excerpt',
  excludes: '',
  inclusive: false,
  spanning: true,

  addStorage() {
    return { live: new Map(), taskStates: new Map(), onIconClick: null, confirmDrop: null, onShortcut: null };
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute('data-excerpt-id'),
        renderHTML: (attrs: Record<string, unknown>) => (attrs.id ? { 'data-excerpt-id': String(attrs.id) } : {}),
      },
      kind: {
        default: 'excerpt',
        parseHTML: (el: HTMLElement) => asKind(el.getAttribute('data-kind')),
        renderHTML: (attrs: Record<string, unknown>) => ({ 'data-kind': asKind(attrs.kind) }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-excerpt-id]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { class: 'excerpt-mark' }), 0];
  },

  addCommands() {
    return {
      setExcerpt: ({ id, kind }) => ({ commands }) => commands.setMark(this.name, { id, kind: asKind(kind) }),
      unsetExcerpt: (id) => ({ tr, dispatch }) => {
        if (dispatch) removeIdMarks(tr, id);
        return true;
      },
      adjustExcerptRange: (id, from, to) => ({ tr, state, dispatch }) => {
        const range = findExcerptRange(state.doc, id);
        if (!range || from >= to) return false;
        let kind: ExcerptKind = 'excerpt';
        state.doc.nodesBetween(range.from, range.to, (n) => {
          const m = n.marks.find((x) => x.type.name === 'excerpt' && x.attrs.id === id);
          if (m) kind = asKind(m.attrs.kind);
        });
        if (dispatch) {
          removeIdMarks(tr, id);
          tr.addMark(from, to, state.schema.marks.excerpt.create({ id, kind }));
          tr.setMeta(SKIP_DROP_CONFIRM, true);
        }
        return true;
      },
      refreshExcerpts: () => ({ tr, dispatch }) => {
        if (dispatch) tr.setMeta(REFRESH, true).setMeta('addToHistory', false);
        return true;
      },
    };
  },

  addKeyboardShortcuts() {
    return {
      'Mod-Shift-e': () => {
        if (!this.storage.onShortcut || this.editor.state.selection.empty) return false;
        this.storage.onShortcut();
        return true;
      },
    };
  },

  addProseMirrorPlugins() {
    const storage = this.storage;
    return [
      new Plugin<DecorationSet>({
        key: excerptPluginKey,
        state: {
          init: (_, state) => buildDecorations(state.doc, storage),
          apply: (tr, old, _prev, next) => (tr.docChanged || tr.getMeta(REFRESH)
            ? buildDecorations(next.doc, storage)
            : old),
        },
        props: {
          decorations: (state) => excerptPluginKey.getState(state),
          transformPasted: (slice: Slice, view: EditorView) => (view.dragging
            ? slice
            : new Slice(stripExcerpts(slice.content), slice.openStart, slice.openEnd)),
        },
        filterTransaction: (tr, state) => {
          if (!tr.docChanged || tr.getMeta(SKIP_DROP_CONFIRM) || tr.getMeta('preventUpdate') || !storage.confirmDrop) return true;
          if (storage.live.size < 2) return true;
          const removed = liveIdsRemoved(state.doc, tr.doc, storage.live);
          return removed < 2 || storage.confirmDrop(removed);
        },
      }),
    ];
  },
});

/** Replaces the host's live set and redraws. */
export function setLiveExcerpts(editor: Editor, live: Map<string, ExcerptKind>): void {
  const storage = editor.storage.excerpt;
  if (!storage) return;
  storage.live = live;
  editor.commands.refreshExcerpts();
}

/** Replaces which task excerpts have their task done or cancelled (#324), and redraws. */
export function setExcerptTaskStates(editor: Editor, states: Map<string, TaskEndState>): void {
  const storage = editor.storage.excerpt;
  if (!storage) return;
  storage.taskStates = states;
  editor.commands.refreshExcerpts();
}
